import apiClient from './client';
import { User, LoginResponse, DashboardData, Trade, MasterTrader, MasterPlatformFilter, CopyRelationship, CopySettings, Signal, Notification, AlpacaOrder, Comment, Subscription, Brokerage, BrokerageConnection, PayoutReport, PayoutReportParams } from '../types';

// Auth API
export const authApi = {
  register: async (data: any): Promise<LoginResponse> => {
    // If data is FormData, send it directly, otherwise it's JSON
    const response = await apiClient.post('/auth/register', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
    });
    return response.data;
  },

  updateProfile: async (data: any): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.put('/auth/profile', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
    });
    return response.data;
  },

  login: async (data: any): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', data);
    return response.data;
  },

  getCurrentUser: async (): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  updateOptionRm: async (optionRm: number): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.put('/auth/option-rm', { optionRm });
    return response.data;
  },

  updateGenesisRm: async (genesisRm: number): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.put('/auth/genesis-rm', { genesisRm });
    return response.data;
  },

  acceptRiskAgreement: async (): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.post('/auth/accept-risk-agreement');
    return response.data;
  },

  acceptMasterAgreement: async (): Promise<{ success: boolean; user: User }> => {
    const response = await apiClient.post('/auth/accept-master-agreement');
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (data: { email?: string; otp?: string; password: string; resetToken?: string }): Promise<{
    success: boolean;
    message?: string;
    token?: string;
    user?: User
  }> => {
    if (data.resetToken) {
      const response = await apiClient.post(`/auth/reset-password/${data.resetToken}`, { password: data.password });
      return response.data;
    }
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  },

  saveOandaToken: async (accessToken: string): Promise<{ success: boolean; message?: string; user: User }> => {
    const response = await apiClient.put('/auth/oanda-token', { accessToken });
    return response.data;
  },

  applyMasterTrader: async (data: any): Promise<{
    success: boolean;
    message?: string;
    application?: { id: string; status: string };
  }> => {
    const response = await apiClient.post('/auth/master-trader-application', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return response.data;
  },

  
};

export interface AffiliatePayoutDetailsInput {
  details: string;
}

export interface AffiliatePayoutDetailsMasked {
  details: string;
  detailsPreview: string;
  updatedAt: string;
}

// Dashboard API
export const dashboardApi = {
  getDashboard: async (brokerId?: string | null, forceRefresh?: boolean): Promise<DashboardData> => {
    const params: any = {};
    if (brokerId) {
      params.brokerId = brokerId;
    }
    if (forceRefresh) {
      params.forceRefresh = 'true';
    }
    const response = await apiClient.get('/dashboard', { params });
    return response.data;
  },

  getMasters: async (brokerId?: string | null): Promise<{
    success: boolean;
    masters: MasterTrader[];
    platformFilters?: MasterPlatformFilter[];
  }> => {
    const params: any = {};
    if (brokerId) {
      params.brokerId = brokerId;
    }
    const response = await apiClient.get('/dashboard/masters', { params });
    return response.data;
  },

  getTopTraderLeaderboard: async (params?: {
    brokerType?: 'all' | 'webull' | 'genesis' | 'forex';
    limit?: number;
  }): Promise<{
    success: boolean;
    rows: Array<{
      rank: number;
      masterId: string;
      name: string;
      avatar: string;
      market?: string;
      pnl: number;
      roi: number;
      winRate: number;
      trades: number;
      followers: number;
      status: string;
    }>;
    summary: {
      totalMasters: number;
      totalFollowers: number;
      avgRoi: number;
      activeTraders: number;
    };
  }> => {
    const response = await apiClient.get('/dashboard/top-traders-leaderboard', { params: params || {} });
    return response.data;
  },

  getAffiliateReferrals: async (): Promise<{
    success: boolean;
    commissionPercent: number;
    oneTimeCommissionPercent: number;
    recurringCommissionPercent: number;
    summary: {
      totalEarnedCents: number;
      pendingCents: number;
      withdrawnCents: number;
      referralCount: number;
    };
    referrals: Array<{
      id: string;
      name: string;
      plan: string;
      status: string;
      profileImage: string;
      joinedAt: string;
    }>;
    commissions: Array<{
      id: string;
      referredUserName: string;
      referredUserPlan: string;
      grossAmountCents: number;
      commissionPercent: number;
      commissionAmountCents: number;
      currency: string;
      status: 'pending' | 'paid';
      description: string;
      createdAt: string;
      occurredAt?: string;
      paidAt: string | null;
    }>;
  }> => {
    const response = await apiClient.get('/dashboard/affiliate/referrals');
    return response.data;
  },

  getAffiliatePayoutDetails: async (): Promise<{
    success: boolean;
    hasPayoutDetails: boolean;
    payoutDetails: AffiliatePayoutDetailsMasked | null;
  }> => {
    const response = await apiClient.get('/dashboard/affiliate/payout-details');
    return response.data;
  },

  saveAffiliatePayoutDetails: async (
    data: AffiliatePayoutDetailsInput,
  ): Promise<{
    success: boolean;
    message: string;
    payoutDetails: AffiliatePayoutDetailsMasked;
  }> => {
    const response = await apiClient.put('/dashboard/affiliate/payout-details', data);
    return response.data;
  },

  getMasterBrokerAccounts: async (masterId: string): Promise<{
    success: boolean;
    brokerAccounts: Array<{
      _id: string;
      brokerageName: string;
      connectionType: string;
      accountType: string;
      accountBalance: number;
      buyingPower: number;
      isPrimary: boolean;
      createdAt: string;
    }>;
  }> => {
    const response = await apiClient.get(`/dashboard/masters/${masterId}/broker-accounts`);
    return response.data;
  },

  /** Master only: own daily following report snapshots (same shape as admin report). */
  getMyMasterFollowingReports: async (params?: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    skip?: number;
  }): Promise<{
    success: boolean;
    reports: Array<{
      _id: string;
      masterId: { _id: string; name: string; email: string; role?: string };
      usersId: Array<{ _id: string; name: string; email: string; role?: string }>;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination?: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get('/dashboard/master-following-reports', { params });
    return response.data;
  },

  /** Master only: own payout report (filters as admin; locked to the logged-in master). */
  getMyMasterPayoutReport: async (params: Omit<PayoutReportParams, 'masterId'>): Promise<PayoutReport> => {
    const response = await apiClient.get('/dashboard/master-following-reports/payout', { params });
    return response.data;
  },

  /** Master only: follower open positions (OANDA, SnapTrade/Webull, GenesisFX), paginated. */
  getFollowersOpenPositions: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    forceRefresh?: boolean;
    brokerId?: string;
  }): Promise<{
    success: boolean;
    masterSequence?: Array<{
      masterId: string;
      masterName: string;
      masterEmail: string;
      followerIds: string[];
    }>;
    positions: Array<{
      connectionId: string;
      connectionType?: string;
      brokerageName?: string;
      productType?: string;
      userId: string;
      userName: string;
      userEmail: string;
      symbol: string;
      qty: number;
      side: string;
      avgEntryPrice: number;
      unrealizedPl: number;
      unrealizedPlPercent: number;
      marketValue: number;
      currentPrice?: number;
      genesisPositionId?: string;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await apiClient.get('/dashboard/followers-open-positions', { params: params || {} });
    return response.data;
  },

  /** Master only: close a follower's open position (OANDA, SnapTrade, or Genesis). */
  closeFollowerPosition: async (params: {
    connectionId: string;
    instrument: string;
    connectionType?: 'oanda' | 'snaptrade' | 'genesis';
    positionId?: string;
    qty?: number;
    side?: 'long' | 'short';
    productType?: string;
    longUnits?: 'ALL' | number;
    shortUnits?: 'ALL' | number;
  }): Promise<{ success: boolean; data?: any }> => {
    const response = await apiClient.post('/dashboard/close-follower-position', params);
    return response.data;
  },

  closeAllFollowersPositions: async (params?: {
    confirm?: string;
  }): Promise<{
    success: boolean;
    results: { succeeded: number; failed: number; errors?: Array<{ symbol?: string; userId?: string; error?: string }> };
    message?: string;
  }> => {
    const response = await apiClient.post('/dashboard/close-all-followers-positions', params || {});
    return response.data;
  },

  /** Close current user's own OANDA position. */
  closeMyPosition: async (params: {
    connectionId: string;
    instrument: string;
    longUnits?: 'ALL' | number;
    shortUnits?: 'ALL' | number;
  }): Promise<{ success: boolean; data?: any }> => {
    const response = await apiClient.post('/dashboard/close-my-position', params);
    return response.data;
  },

  getAccountAllocation: async (): Promise<{
    success: boolean;
    accounts: Array<{
      id: string;
      brokerageName: string;
      connectionType: string;
      accountType?: string;
      isPrimary: boolean;
      accountBalance: number;
      usedAllocation: number;
      remainingAllocation: number;
    }>;
  }> => {
    const response = await apiClient.get('/dashboard/copy-trading/account-allocation');
    return response.data;
  },

  enableCopyTrading: async (data: CopySettings & { followerBrokerAccountId?: string }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    copyRelationship?: any;
  }> => {
    const response = await apiClient.post('/dashboard/copy-trading/enable', data);
    return response.data;
  },

  enableCopyTradingBrokerAccounts: async (data: {
    masterUserId: string;
    masterBrokerAccountIds: string[];
    allocationPercent: number;
    maxInvestmentAmount?: number;
    endDate?: string;
    followerBrokerAccountId?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    relationships?: Array<{
      id: string;
      master: string;
      masterBrokerAccountId: string;
      allocationPercent: number;
      maxInvestmentAmount?: number;
      startDate: string;
      endDate?: string;
    }>;
  }> => {
    const response = await apiClient.post('/dashboard/copy-trading/enable-broker-accounts', data);
    return response.data;
  },

  cancelCopyTrading: async (relationshipId: string, closePositions: boolean): Promise<{
    success: boolean;
    message?: string;
    closedPositions?: number;
    errors?: string[];
  }> => {
    const response = await apiClient.post('/dashboard/copy-trading/cancel', {
      relationshipId,
      closePositions,
    });
    return response.data;
  },

  /** Pause all active copy relationships until end of US trading day (ET); auto-resumes. */
  pauseCopyTradingForDay: async (): Promise<{
    success: boolean;
    message?: string;
    alreadyPaused?: boolean;
    pausedCount?: number;
    copyTradingResumeAt?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/dashboard/copy-trading/pause-for-day');
    return response.data;
  },

  updateCopySettings: async (data: {
    relationshipId: string;
    allocationPercent?: number;
    maxInvestmentAmount?: number;
    endDate?: string;
  }): Promise<{ success: boolean; message?: string; relationship?: CopyRelationship }> => {
    const response = await apiClient.put('/dashboard/copy-trading/update', data);
    return response.data;
  },

  getCopyRelationships: async (
    brokerId?: string | null,
    opts?: { allBrokers?: boolean }
  ): Promise<{
    success: boolean;
    relationships: CopyRelationship[]
  }> => {
    const params: any = {};
    if (opts?.allBrokers) {
      params.allBrokers = true;
    } else if (brokerId) {
      params.brokerId = brokerId;
    }
    const response = await apiClient.get('/dashboard/copy-trading/relationships', { params });
    return response.data;
  },

  getCopyPerformance: async (relationshipId: string): Promise<{
    success: boolean;
    relationship: CopyRelationship;
    trades: Trade[];
    performance: {
      totalInvested: number;
      totalProfit: number;
      roi: string;
      totalTrades: number;
      successfulTrades: number;
      failedTrades: number;
    };
  }> => {
    const response = await apiClient.get(`/dashboard/copy-trading/performance/${relationshipId}`);
    return response.data;
  },
};

// Trades API
export const tradesApi = {
  getTrades: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    brokerId?: string | null;
    days?: number;
  }): Promise<{
    success: boolean;
    trades: Trade[];
    totalPages: number;
    currentPage: number;
    total: number
  }> => {
    const response = await apiClient.get('/trades', { params });
    return response.data;
  },

  /** SnapTrade: fetch last 24h activities and persist new rows to WebullTradesHistory. */
  syncWebullLast24: async (brokerId: string): Promise<{
    success: boolean;
    accountId?: string;
    fetched?: number;
    inserted?: number;
    skipped?: number;
    source?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/trades/webull/last24', { brokerId });
    return response.data;
  },

  getPnlCalendar: async (params?: {
    month?: number;
    year?: number;
    brokerId?: string | null;
    /** When set, returns that user's master-trade P&L (for master profile / followers). */
    masterUserId?: string | null;
  }): Promise<{
    success: boolean;
    monthlyNetTotal: number;
    annualNetTotal: number;
    dailyPerformance: Array<{
      date: string;
      day: number;
      pnl: number;
      tradesCount: number;
    }>;
    statistics: {
      winRate: number;
      winLossRatio: string;
      totalTrades: number;
      bestDay: number;
      worstDay: number;
      bestTrade: number;
      worstTrade: number;
    };
  }> => {
    const response = await apiClient.get('/trades/pnl-calendar', { params });
    return response.data;
  },
};

// Admin API
export interface AdminAffiliate {
  _id: string;
  name: string;
  email: string;
  role: User['role'];
  plan?: string;
  ableToAffiliate: boolean;
  affiliateClientAccess?: boolean;
  affiliateCommissionPercent: number;
  affiliateOneTimeCommissionPercent?: number;
  affiliateRecurringCommissionPercent?: number;
  referralCount: number;
  totalEarnedCents: number;
  withdrawnCents: number;
  pendingCents: number;
  hasPayoutDetails: boolean;
}

export interface MasterTraderApplication {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  tradingPlatform: 'forex' | 'webull';
  tradingExperience: string;
  pnlUpload: {
    url: string;
    originalName?: string;
    mimeType?: string;
  };
  communicationMethod: string;
  paymentDetails: string;
  webullApiKey: string;
  webullApiSecret: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: string;
}

export const adminApi = {
  getStats: async (): Promise<{ success: boolean; stats: { totalUsers: number; mastersCount: number; activeSubscriptions: number; totalBalance: number } }> => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  /** Admin: combined platform realized P&L for a time scope. */
  getPlatformPnl: async (params: {
    scope: 'today' | 'yesterday' | 'custom';
    startDate?: string;
    endDate?: string;
  }): Promise<{
    success: boolean;
    userCounts?: { masters: number; followers: number };
    /** Backend may return one of these keys depending on implementation. */
    period?: { platformTotalPnl: number };
    today?: { platformTotalPnl: number };
    summary?: { platformTotalPnl: number };
  }> => {
    const response = await apiClient.get('/admin/platform-pnl', { params });
    return response.data;
  },

  /** Admin: per-user realized P&L (masters/followers) for a time scope. */
  getPlatformPnlUsers: async (params: {
    role: 'all' | 'master' | 'follower';
    scope: 'today' | 'yesterday' | 'custom';
    startDate?: string;
    endDate?: string;
    minPnl?: number;
    maxPnl?: number;
  }): Promise<{
    success: boolean;
    rows: Array<{
      userId: string;
      role: 'master' | 'follower';
      name: string;
      email?: string;
      parentMasterName?: string;
      totalTrades?: number;
      totalPnl?: number;
    }>;
  }> => {
    const response = await apiClient.get('/admin/platform-pnl-users', { params });
    return response.data;
  },

  getPendingMasters: async (): Promise<{ success: boolean; users: User[] }> => {
    const response = await apiClient.get('/admin/pending-masters');
    return response.data;
  },

  getMasterTraderApplications: async (
    status?: 'pending' | 'approved' | 'rejected' | 'cancelled',
  ): Promise<{
    success: boolean;
    applications: MasterTraderApplication[];
  }> => {
    const response = await apiClient.get('/admin/master-trader-applications', {
      params: status ? { status } : {},
    });
    return response.data;
  },

  getMasterTraderApplicationDetails: async (
    applicationId: string,
  ): Promise<{ success: boolean; application: MasterTraderApplication & { webullApiKey: string; webullApiSecret: string } }> => {
    const response = await apiClient.get(`/admin/master-trader-applications/${applicationId}`);
    return response.data;
  },

  approveMasterTraderApplication: async (applicationId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(`/admin/master-trader-applications/${applicationId}/approve`);
    return response.data;
  },

  cancelMasterTraderApplication: async (applicationId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(`/admin/master-trader-applications/${applicationId}/cancel`);
    return response.data;
  },

  approveMaster: async (userId: string, tradingType: 'swing' | 'day'): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(`/admin/approve-master/${userId}`, { tradingType });
    return response.data;
  },

  rejectMaster: async (userId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(`/admin/reject-master/${userId}`);
    return response.data;
  },

  getPendingFollowers: async (): Promise<{ success: boolean; users: User[] }> => {
    const response = await apiClient.get('/admin/pending-followers');
    return response.data;
  },

  approveFollower: async (userId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(`/admin/approve-follower/${userId}`);
    return response.data;
  },

  rejectFollower: async (userId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(`/admin/reject-follower/${userId}`);
    return response.data;
  },

  getAllUsers: async (params?: {
    page?: number;
    limit?: number;
    role?: string;
    subscription?: string;
    search?: string;
  }): Promise<{ success: boolean; users: User[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const response = await apiClient.get('/admin/all-users', { params });
    return response.data;
  },

  getAffiliates: async (search?: string): Promise<{
    success: boolean;
    affiliates: AdminAffiliate[];
  }> => {
    const response = await apiClient.get('/admin/affiliates', {
      params: search ? { search } : undefined,
    });
    return response.data;
  },

  updateAffiliate: async (
    userId: string,
    data: {
      enabled: boolean;
      commissionPercent?: number;
      oneTimeCommissionPercent: number;
      recurringCommissionPercent: number;
      clientAccess?: boolean;
    },
  ): Promise<{ success: boolean; message?: string; user?: User }> => {
    const response = await apiClient.patch(`/admin/affiliate/${userId}`, data);
    return response.data;
  },

  markAffiliatePaid: async (
    userId: string,
  ): Promise<{ success: boolean; message?: string; amountPaidCents?: number }> => {
    const response = await apiClient.post(`/admin/affiliate/${userId}/mark-paid`);
    return response.data;
  },

  getAffiliatePayoutDetails: async (userId: string): Promise<{
    success: boolean;
    user: { id: string; name: string; email: string };
    payoutDetails: { details: string; updatedAt: string };
  }> => {
    const response = await apiClient.get(`/admin/affiliate/${userId}/payout-details`);
    return response.data;
  },

  getTermsTemplates: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{
    success: boolean;
    users: any[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await apiClient.get('/admin/terms-templates', { params });
    return response.data;
  },

  getMasters: async (params?: {
    brokerFilter?: string;
  }): Promise<{ success: boolean; users: User[] }> => {
    const response = await apiClient.get('/admin/masters', { params });
    return response.data;
  },

  getMasterFollowingReports: async (params?: {
    masterId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    skip?: number;
  }): Promise<{
    success: boolean;
    reports: Array<{
      _id: string;
      masterId: { _id: string; name: string; email: string; role?: string };
      usersId: Array<{ _id: string; name: string; email: string; role?: string }>;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination?: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get('/admin/master-following-reports', { params });
    return response.data;
  },

  getMasterPayoutReport: async (params: PayoutReportParams): Promise<PayoutReport> => {
    const response = await apiClient.get('/admin/master-following-reports/payout', { params });
    return response.data;
  },

  setMasterPayoutRate: async (
    userId: string,
    monthlyRate: number,
  ): Promise<{ success: boolean; message?: string; user?: Partial<User> }> => {
    const response = await apiClient.patch(`/admin/master/${userId}/payout-rate`, { monthlyRate });
    return response.data;
  },

  triggerMasterFollowingReport: async (): Promise<{
    success: boolean;
    message: string;
    reports: any[];
  }> => {
    const response = await apiClient.post('/admin/master-following-reports/trigger');
    return response.data;
  },

  createMaster: async (data: {
    userId?: string;
    name?: string;
    email?: string;
    password?: string;
    tradingType?: 'swing' | 'day';
  }): Promise<{ success: boolean; message?: string; user?: User }> => {
    const response = await apiClient.post('/admin/create-master', data);
    return response.data;
  },

  demoteMaster: async (userId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.delete(`/admin/master/${userId}`);
    return response.data;
  },

  setMasterRequireRecurringPayment: async (
    userId: string,
    enabled: boolean,
  ): Promise<{ success: boolean; message?: string; user?: User }> => {
    const response = await apiClient.patch(`/admin/master/${userId}/require-recurring-payment`, {
      enabled,
    });
    return response.data;
  },

  setMasterFollowFee: async (
    userId: string,
    data: {
      hasFee: boolean;
      productsByPlan?: {
        og?: string;
        old_user?: string;
        growth?: string;
        general?: string;
        pro?: string;
        'if&brice_user'?: string;
      };
    },
  ): Promise<{ success: boolean; message?: string; user?: User }> => {
    const response = await apiClient.patch(`/admin/master/${userId}/follow-fee`, data);
    return response.data;
  },

  deleteUser: async (userId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.delete(`/admin/user/${userId}`);
    return response.data;
  },

  /** Log in as another user (admin only). Returns { token, user } to set in auth store. */
  loginAs: async (userId: string): Promise<{ success: boolean; token: string; user: User }> => {
    const response = await apiClient.post(`/admin/login-as/${userId}`);
    return response.data;
  },

  /** Get OANDA open positions (admin only), paginated. */
  getOandaOpenPositions: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    forceRefresh?: boolean;
    brokerFilter?: 'all' | 'oanda' | 'webull' | 'genesis';
  }): Promise<{
    success: boolean;
    masterSequence?: Array<{
      masterId: string;
      masterName: string;
      masterEmail: string;
      followerIds: string[];
    }>;
    positions: Array<{
      connectionId: string;
      connectionType?: string;
      brokerageName?: string;
      userId: string;
      userName: string;
      userEmail: string;
      accountId?: string;
      snapshotId?: string;
      oandaAccountId?: string;
      snaptradeAccountId?: string;
      symbol: string;
      qty: number;
      side: string;
      avgEntryPrice: number;
      unrealizedPl: number;
      unrealizedPlPercent: number;
      marketValue: number;
      currentPrice?: number;
      productType?: string;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
    warnings?: string[];
  }> => {
    const response = await apiClient.get('/admin/open-positions', { params: params || {} });
    return response.data;
  },

  /** Admin: selected master's followers — OANDA + SnapTrade/Webull open positions (paginated). */
  getMasterFollowersOpenPositions: async (params: {
    masterId: string;
    page?: number;
    limit?: number;
    search?: string;
    forceRefresh?: boolean;
    brokerFilter?: 'all' | 'oanda' | 'webull' | 'genesis';
  }): Promise<{
    success: boolean;
    master: { id: string; name: string; email: string };
    followers: Array<{
      relationshipId: string;
      status: string;
      allocationPercent: number;
      followerId: string;
      followerName: string;
      followerEmail: string;
    }>;
    positions: Array<{
      connectionId: string;
      connectionType: string;
      brokerageName: string;
      userId: string;
      userName: string;
      userEmail: string;
      oandaAccountId?: string;
      snaptradeAccountId?: string;
      symbol: string;
      qty: number;
      side: string;
      avgEntryPrice: number;
      unrealizedPl: number;
      unrealizedPlPercent: number;
      marketValue: number;
      currentPrice?: number;
      productType?: string;
      accountId?: string;
      snapshotId?: string;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
    warnings?: string[];
  }> => {
    const response = await apiClient.get('/admin/master-followers-open-positions', { params });
    return response.data;
  },

  closeFollowerPositionAdmin: async (params: {
    connectionId: string;
    instrument: string;
    connectionType?: 'oanda' | 'snaptrade' | 'genesis';
    positionId?: string;
    qty?: number;
    side?: 'long' | 'short';
    productType?: string;
    masterId?: string;
    longUnits?: 'ALL' | number;
    shortUnits?: 'ALL' | number;
  }): Promise<{ success: boolean; data?: any; order?: any; trade?: any; message?: string }> => {
    const response = await apiClient.post('/admin/close-follower-position', params);
    return response.data;
  },

  closeAllFollowersPositionsAdmin: async (params: {
    masterId?: string;
    closeAllPlatform?: boolean;
    confirm?: string;
  }): Promise<{
    success: boolean;
    results: { succeeded: number; failed: number; errors: Array<{ symbol?: string; userId?: string; error?: string }> };
    message?: string;
  }> => {
    const response = await apiClient.post('/admin/close-all-followers-positions', params);
    return response.data;
  },

  /** Close an OANDA position (admin only). longUnits or shortUnits: "ALL" or number. */
  closeOandaPosition: async (params: {
    connectionId: string;
    instrument: string;
    longUnits?: 'ALL' | number;
    shortUnits?: 'ALL' | number;
  }): Promise<{ success: boolean; data?: any }> => {
    const response = await apiClient.post('/admin/oanda-close-position', params);
    return response.data;
  },

  getUserStatistics: async (userId: string): Promise<{
    success: boolean;
    statistics: {
      user: {
        _id: string;
        name: string;
        email: string;
        role: string;
        accountBalance: number;
      };
      tradeStats: {
        totalTrades: number;
        successfulTrades: number;
        failedTrades: number;
        totalProfitLoss: number;
        todayPnL?: number;
        weeklyPnL?: number;
        monthlyPnL?: number;
        winRate: number;
        profitableTrades: number;
        losingTrades: number;
      };
      brokerageConnections: Array<{
        _id: string;
        brokerageId: string;
        brokerageName: string;
        brokerageSlug: string;
        connectionType: string;
        accountId?: string;
        accountName?: string;
        accountType: string;
        accountBalance: number;
        buyingPower: number;
        currency: string;
        status: string;
        isPrimary: boolean;
        lastSynced?: string;
        createdAt: string;
      }>;
      followers: Array<{
        follower: {
          _id: string;
          name: string;
          email: string;
          accountBalance: number;
        };
        relationship: {
          allocationPercent: number;
          status: string;
        };
        stats: {
          totalTrades: number;
          successfulTrades: number;
          totalProfitLoss: number;
          winRate: number;
          profitableTrades: number;
          losingTrades: number;
        };
      }>;
    };
  }> => {
    const response = await apiClient.get(`/admin/user-statistics/${userId}`);
    return response.data;
  },

  getUserTrades: async (userId: string, page?: number, limit?: number): Promise<{
    success: boolean;
    trades: Trade[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get(`/admin/user-trades/${userId}`, {
      params: { page, limit }
    });
    return response.data;
  },

  getUserFollowers: async (userId: string, page?: number, limit?: number): Promise<{
    success: boolean;
    followers: Array<{
      follower: {
        _id: string;
        name: string;
        email: string;
        accountBalance: number;
      };
      relationship: {
        allocationPercent: number;
        status: string;
      };
      stats: {
        totalTrades: number;
        successfulTrades: number;
        totalProfitLoss: number;
        winRate: number;
        profitableTrades: number;
        losingTrades: number;
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get(`/admin/user-followers/${userId}`, {
      params: { page, limit }
    });
    return response.data;
  },

  // Subscription Management
  getSubscriptions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<{
    success: boolean;
    users: any[];
    stats: {
      total: number;
      active: number;
      expired: number;
      cancelled: number;
      pending: number;
      none: number;
    };
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await apiClient.get('/admin/subscriptions', { params });
    return response.data;
  },

  getUserCopyRelationships: async (userId: string): Promise<{
    success: boolean;
    copies: Array<{
      _id: string;
      masterName: string;
      brokerName: string;
      allocationPercent: number;
      status: string;
    }>;
  }> => {
    const response = await apiClient.get(`/admin/subscriptions/${userId}/copies`);
    return response.data;
  },

  bulkUpdateSubscriptions: async (data: {
    userIds: string[];
    action: 'activate' | 'deactivate';
    duration?: { type: 'days' | 'weeks' | 'months'; value: number };
  }): Promise<{
    success: boolean;
    message: string;
    results: {
      success: Array<{ userId: string; name: string; email: string; action: string; endDate?: string }>;
      failed: Array<{ userId: string; name?: string; error: string }>;
    };
  }> => {
    const response = await apiClient.post('/admin/subscriptions/bulk-update', data);
    return response.data;
  },

  setUsersOtherPro: async (data: {
    userIds?: string[];
    userId?: string;
  }): Promise<{
    success: boolean;
    message: string;
    results: {
      success: Array<{ userId: string; name: string; email: string; action: string }>;
      failed: Array<{ userId: string; name?: string; email?: string; error: string }>;
    };
  }> => {
    const response = await apiClient.post('/admin/subscriptions/set-other-pro', data);
    return response.data;
  },

  updateSingleSubscription: async (data: {
    userId: string;
    action: 'activate' | 'deactivate';
    plan?:
      | 'growth'
      | 'general'
      | 'pro'
      | 'og'
      | 'special_users'
      | 'old_user'
      | 'if&brice_user'
      | 'other_pro'
      | 'full_access'
      | 'full_access_paid'
      | 'general_599'
      | 'premium_999';
    duration?: { type: 'days' | 'weeks' | 'months'; value: number };
  }): Promise<{
    success: boolean;
    message: string;
    subscription: any;
  }> => {
    const response = await apiClient.post('/admin/subscriptions/update-single', data);
    return response.data;
  },

  grantWhopTrial: async (data: {
    userId: string;
    plan?: 'growth' | 'general' | 'pro' | 'custom' | 'og' | 'special_users' | 'old_user' | 'full_access_paid' | 'full_access';
    amount?: number;
    trialDays?: number;
    maxWebullAccounts?: number;
    maxGenesisAccounts?: number;
    maxBrokers?: number;
    maxMastersPerAccount?: number | null;
  }): Promise<{
    success: boolean;
    message: string;
    subscription: any;
  }> => {
    const response = await apiClient.post('/admin/subscriptions/grant-whop-trial', data);
    return response.data;
  },

  deleteSubscription: async (data: {
    userId: string;
  }): Promise<{
    success: boolean;
    message: string;
    deletedIds?: string[];
    whop?: string[];
  }> => {
    const response = await apiClient.post('/admin/subscriptions/delete', data);
    return response.data;
  },

  extendSubscriptions: async (data: {
    userIds: string[];
    duration: { type: 'days' | 'weeks' | 'months'; value: number };
  }): Promise<{
    success: boolean;
    message: string;
    results: {
      success: Array<{ userId: string; name: string; email: string; newEndDate: string }>;
      failed: Array<{ userId: string; error: string }>;
    };
  }> => {
    const response = await apiClient.post('/admin/subscriptions/extend', data);
    return response.data;
  },

  getEodReports: async (params?: {
    startDate?: string;
    endDate?: string;
    role?: 'master' | 'follower';
  }): Promise<{
    success: boolean;
    reports: Array<{
      userId: string;
      userName: string;
      userEmail: string;
      role: 'master' | 'follower';
      tradingType?: 'swing' | 'day';
      totalTrades: number;
      successfulTrades: number;
      failedTrades: number;
      totalProfitLoss: number;
      winRate: number;
      accountBalance: number;
      brokerageName?: string;
      dateRange: {
        start: string;
        end: string;
      };
    }>;
  }> => {
    const response = await apiClient.get('/admin/eod-reports', { params });
    return response.data;
  },

  downloadEodReportsExcel: async (params?: {
    startDate?: string;
    endDate?: string;
    role?: 'master' | 'follower';
    swingTrader?: boolean;
    dayTrader?: boolean;
    userSearch?: string;
    statusFilter?: string;
  }): Promise<void> => {
    try {
      console.log('📥 [API] Downloading EOD report:', params);
      
      // Convert boolean values to strings for query params
      const queryParams: any = {};
      if (params?.startDate) queryParams.startDate = params.startDate;
      if (params?.endDate) queryParams.endDate = params.endDate;
      if (params?.role) queryParams.role = params.role;
      if (params?.swingTrader === true) queryParams.swingTrader = 'true';
      if (params?.dayTrader === true) queryParams.dayTrader = 'true';
      if (params?.userSearch) queryParams.userSearch = params.userSearch;
      if (params?.statusFilter) queryParams.statusFilter = params.statusFilter;
      
      console.log('📥 [API] Query params:', queryParams);
      
      const response = await apiClient.get('/admin/eod-reports/download', {
        params: queryParams,
        responseType: 'blob',
      });
      
      console.log('📥 [API] Response received:', {
        status: response.status,
        headers: response.headers,
        dataSize: response.data?.size || 0,
        dataType: response.data?.type || 'unknown'
      });
      
      // Check if response is actually a blob
      // Allow empty blobs (empty Excel files) - they're valid responses
      if (!response.data) {
        console.warn('⚠️  [API] No response data received');
        throw new Error('No response received from server.');
      }
      
      // Empty Excel files are valid (when no data matches filters)
      if (response.data instanceof Blob && response.data.size === 0) {
        console.warn('⚠️  [API] Empty Excel file received (no data matches filters)');
        // Still allow download - empty Excel file is valid
      }
      
      // Create blob and download (Excel format)
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      
      console.log('📥 [API] Blob created:', {
        size: blob.size,
        type: blob.type
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date range
      const startDate = params?.startDate || new Date().toISOString().split('T')[0];
      const endDate = params?.endDate || new Date().toISOString().split('T')[0];
      const roleSuffix = params?.role ? `-${params.role}` : '';
      link.download = `eod-reports-${startDate}${roleSuffix}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ [API] File download triggered');
    } catch (error: any) {
      console.error('❌ [API] Download error:', error);
      
      // If it's a blob error, try to read it as text
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        console.error('❌ [API] Error response text:', text);
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || 'Failed to download report');
        } catch (parseError) {
          throw new Error(text || 'Failed to download report');
        }
      }
      
      throw error;
    }
  },

  getEodCounts: async (params?: {
    date?: string;
  }): Promise<{
    success: boolean;
    masterCount: number;
    followerCount: number;
  }> => {
    const response = await apiClient.get('/admin/eod-counts', { params });
    return response.data;
  },

  getEodReportsData: async (params?: {
    startDate?: string;
    endDate?: string;
    role?: 'master' | 'follower';
    statusFilter?: 'pending' | 'failed' | 'filled' | 'cancelled';
    swingTrader?: boolean;
    dayTrader?: boolean;
  }): Promise<{
    success: boolean;
    title: string;
    dateRange: {
      start: string;
      end: string;
    };
    role: string;
    reports: Array<{
      user: {
        role: string;
        name: string;
        email: string;
      };
      summary: {
        totalTrades: number;
        buy: number;
        sell: number;
        broker: string;
        totalFilled: number;
        totalCancel: number;
        totalFailed?: number;
      };
      trades: Array<{
        symbol: string;
        orderType: string;
        status: string;
        type: string;
        price: string;
        masterFollowing?: string | null;
        time: string;
        cancel: string;
      }>;
    }>;
  }> => {
    const response = await apiClient.get('/admin/eod-reports/data', { params });
    return response.data;
  },

  /** Admin: trigger EOW summary emails. */
  sendTestEowAlert: async (): Promise<{
    success: boolean;
    message?: string;
    result?: { sent: number; failed: number; total: number };
    error?: string;
  }> => {
    const response = await apiClient.post('/admin/alerts/test-eow');
    return response.data;
  },

  getEodHistory: async (): Promise<{
    success: boolean;
    history: Array<{
      date: string;
      masterCount: number;
      followerCount: number;
    }>;
  }> => {
    const response = await apiClient.get('/admin/eod-history');
    return response.data;
  },

  getEodReportsList: async (params?: {
    limit?: number;
    role?: 'master' | 'follower' | 'all';
  }): Promise<{
    success: boolean;
    reports: Array<{
      _id: string;
      reportDate: string;
      fileName: string;
      fileSize: number;
      role: string;
      masterCount: number;
      followerCount: number;
      totalTrades: number;
      status: string;
      downloadUrl: string;
      createdAt: string;
    }>;
  }> => {
    const response = await apiClient.get('/admin/eod-reports/list', { params });
    return response.data;
  },

  downloadEodReportFile: async (reportId: string): Promise<void> => {
    const response = await apiClient.get(`/admin/eod-reports/download-file/${reportId}`, {
      responseType: 'blob',
    });
    
    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `eod-report-${reportId}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  generateEodReport: async (params: {
    date: string;
    role?: 'master' | 'follower' | 'all';
  }): Promise<{
    success: boolean;
    message: string;
    reportDate: string;
    role: string;
  }> => {
    const response = await apiClient.post('/admin/eod-reports/generate', params);
    return response.data;
  },

  verifyEodTrades: async (params?: {
    limit?: number;
  }): Promise<{
    success: boolean;
    summary: {
      today: {
        masterTrades: number;
        followerTrades: number;
      };
      yesterday: {
        masterTrades: number;
        followerTrades: number;
      };
    };
    recentTrades: Array<{
      id: string;
      symbol: string;
      side: string;
      qty: number;
      tradeType: string;
      status: string;
      createdAt: string;
      user: {
        name: string;
        email: string;
        role: string;
      };
      readyForEod: string;
    }>;
  }> => {
    const response = await apiClient.get('/admin/eod-reports/verify-trades', { params });
    return response.data;
  },

  testEodReport: async (params?: {
    date?: string;
  }): Promise<{
    success: boolean;
    message: string;
    tradesForDate: number;
    availableDates: Array<{
      date: string;
      totalTrades: number;
    }>;
    reports: Array<{
      _id: string;
      fileName: string;
      reportDate: string;
      role: string;
      downloadUrl: string;
      status: string;
      totalTrades: number;
    }>;
  }> => {
    const response = await apiClient.post('/admin/eod-reports/test', params || {});
    return response.data;
  },


  checkEodDates: async (): Promise<{
    success: boolean;
    totalTrades: number;
    availableDates: Array<{
      date: string;
      totalTrades: number;
      masterTrades: number;
      followerTrades: number;
    }>;
  }> => {
    const response = await apiClient.get('/admin/eod-reports/check-dates');
    return response.data;
  },
};

// Signals API
export const signalsApi = {
  createSignal: async (data: {
    type: 'signal' | 'post';
    symbol?: string;
    price?: number;
    side?: 'buy' | 'sell';
    target?: number;
    stopLoss?: number;
    description: string;
  }): Promise<{ success: boolean; signal: Signal }> => {
    const response = await apiClient.post('/signals', data);
    return response.data;
  },

  getSignals: async (page?: number, limit?: number, type?: 'signal' | 'post'): Promise<{
    success: boolean;
    signals: Signal[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get('/signals', {
      params: { page, limit, type }
    });
    return response.data;
  },

  getMasterSignals: async (masterId: string, page?: number, limit?: number, type?: 'signal' | 'post'): Promise<{
    success: boolean;
    signals: Signal[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get(`/signals/master/${masterId}`, {
      params: { page, limit, type }
    });
    return response.data;
  },

  likeSignal: async (signalId: string): Promise<{
    success: boolean;
    isLiked: boolean;
    likesCount: number;
    likes?: string[];
  }> => {
    const response = await apiClient.post(`/signals/${signalId}/like`);
    return response.data;
  },

  deleteSignal: async (signalId: string): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const response = await apiClient.delete(`/signals/${signalId}`);
    return response.data;
  },
};

// Notifications API
export const notificationsApi = {
  getNotifications: async (page?: number, limit?: number, filter?: 'all' | 'unread' | 'read'): Promise<{
    success: boolean;
    notifications: Notification[];
    unreadCount: number;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await apiClient.get('/notifications', {
      params: { page, limit, filter }
    });
    return response.data;
  },

  getUnreadCount: async (): Promise<{
    success: boolean;
    unreadCount: number;
  }> => {
    const response = await apiClient.get('/notifications/unread-count');
    return response.data;
  },

  markAsRead: async (notificationId: string): Promise<{
    success: boolean;
    notification: Notification;
  }> => {
    const response = await apiClient.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async (): Promise<{
    success: boolean;
    updatedCount: number;
  }> => {
    const response = await apiClient.put('/notifications/read-all');
    return response.data;
  },

  deleteNotification: async (notificationId: string): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const response = await apiClient.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

// Comments API
export const commentsApi = {
  createComment: async (data: {
    signalId: string;
    text: string;
    parentCommentId?: string;
  }): Promise<{
    success: boolean;
    comment: Comment;
  }> => {
    const response = await apiClient.post('/comments', data);
    return response.data;
  },

  getComments: async (signalId: string): Promise<{
    success: boolean;
    comments: Comment[];
  }> => {
    const response = await apiClient.get(`/comments/signal/${signalId}`);
    return response.data;
  },

  likeComment: async (commentId: string): Promise<{
    success: boolean;
    isLiked: boolean;
    likesCount: number;
    likes: string[];
  }> => {
    const response = await apiClient.post(`/comments/${commentId}/like`);
    return response.data;
  },

  deleteComment: async (commentId: string): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const response = await apiClient.delete(`/comments/${commentId}`);
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsApi = {
  getPublishableKey: async (): Promise<{
    success: boolean;
    publishableKey: string;
  }> => {
    const response = await apiClient.get('/subscriptions/publishable-key');
    return response.data;
  },

  createCheckoutSession: async (
    planOrOptions?:
      | 'growth'
      | 'general'
      | 'pro'
      | 'custom'
      | 'masterfee'
      | 'masterfollowingfee'
      | 'og'
      | 'special_users'
      | 'old_user'
      | 'full_access_paid'
      | {
          plan?:
            | 'growth'
            | 'general'
            | 'pro'
            | 'custom'
            | 'masterfee'
            | 'masterfollowingfee'
            | 'og'
            | 'special_users'
            | 'old_user'
            | 'full_access_paid';
          billingMode?: 'trial' | 'monthly_instant' | 'onetime_next_month';
          masterId?: string;
        }
  ): Promise<{
    success: boolean;
    checkoutUrl: string;
    sessionId: string;
  }> => {
    const payload =
      typeof planOrOptions === 'string' || planOrOptions == null
        ? { plan: planOrOptions }
        : planOrOptions;
    const response = await apiClient.post('/subscriptions/create-checkout', payload, {
      timeout: 25000,
    });
    const data = response.data || {};
    const checkoutUrl = data.checkoutUrl || data.url;
    if (!checkoutUrl) {
      throw new Error(data.message || 'Checkout URL was not returned.');
    }
    window.location.assign(checkoutUrl);
    return { ...data, checkoutUrl };
  },

  getSubscriptionStatus: async (): Promise<{
    success: boolean;
    hasSubscription: boolean;
    isActive: boolean;
    canAccessPremiumFeatures?: boolean;
    cancelAtPeriodEnd?: boolean;
    trialEligible?: boolean;
    trialEligibilityReason?: string | null;
    growthEligible?: boolean;
    growthEligibilityReason?: string | null;
    subscription: Subscription | null;
    masterFeeSubscription?: Subscription | null;
  }> => {
    const response = await apiClient.get(
      '/subscriptions/status' + (typeof window !== 'undefined' ? window.location.search : '')
    );
    return response.data;
  },

  activatePending: async (): Promise<{
    success: boolean;
    message?: string;
    subscription?: Subscription;
  }> => {
    const response = await apiClient.post('/subscriptions/activate-pending');
    return response.data;
  },

  cancelSubscription: async (type?: 'platform' | 'masterfee'): Promise<{
    success: boolean;
    message?: string;
    accessUntil?: string;
  }> => {
    const response = await apiClient.post('/subscriptions/cancel', type ? { type } : {});
    return response.data;
  },

  resumeSubscription: async (type?: 'platform' | 'masterfee'): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const response = await apiClient.post('/subscriptions/resume', type ? { type } : {});
    return response.data;
  },
};

// Brokerage API
export const brokerageApi = {
  getSupportedBrokerages: async (region?: string): Promise<{
    success: boolean;
    brokerages: Brokerage[];
  }> => {
    const response = await apiClient.get('/brokerage/supported');
    return response.data;
  },

  getConnections: async (): Promise<{
    success: boolean;
    connections: BrokerageConnection[];
  }> => {
    const response = await apiClient.get('/brokerage/connections');
    return response.data;
  },

  UserLoginGenesis: async (email: string, password: string): Promise<{
    success: boolean;
    message: string;
    data?: {
      email: string;
      accounts: Array<{
        id: string;
        accountNumber: string;
        displayName?: string;
        name?: string;
        currency: string;
        balance: number | null;
      }>;
      connection?: {
        id: string;
        brokerageName: string;
        connectionType: string;
      };
    };
  }> => {
    const response = await apiClient.post('/brokerage/connect/genesis', {
      email,
      password,
    });
    return response.data;
  },

  connectAlpaca: async (
    apiKey: string,
    apiSecret: string,
    isPaper: boolean,
  ): Promise<{
    success: boolean;
    message?: string;
    connection?: BrokerageConnection;
    account?: { id: string; equity: number; buyingPower: number; accountType: string };
    user?: User;
    error?: string;
  }> => {
    const response = await apiClient.post('/brokerage/connect/alpaca', {
      apiKey,
      apiSecret,
      isPaper,
    });
    return response.data;
  },

  selectGenesisAccount: async (
    connectionId: string,
    accountId: string,
    accountNumber: string,
  ): Promise<{
    success: boolean;
    message: string;
    data?: {
      connection: {
        id: string;
        brokerageName: string;
        connectionType: string;
        accountId: string;
        accountNumber: string;
        accountName?: string;
      };
    };
  }> => {
    const response = await apiClient.post('/brokerage/connect/genesis/select-account', {
      connectionId,
      accountId,
      accountNumber,
    });
    return response.data;
  },

  initSnaptradeConnection: async (brokerageId: string): Promise<{
    success: boolean;
    redirectUrl: string;
    brokerage: { id: string; name: string };
  }> => {
    const redirectBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const response = await apiClient.post('/brokerage/connect/snaptrade/init', {
      brokerageId,
      redirectBaseUrl
    });
    return response.data;
  },

  completeSnaptradeConnection: async (brokerageId: string): Promise<{
    success: boolean;
    message?: string;
    connection?: BrokerageConnection;
  }> => {
    const response = await apiClient.post('/brokerage/connect/snaptrade/callback', { brokerageId });
    return response.data;
  },

  setPrimaryConnection: async (connectionId: string): Promise<{
    success: boolean;
    message?: string;
    connection?: BrokerageConnection;
    activeRelationships?: Array<{
      master: {
        _id: string;
        name: string;
        email: string;
      };
      brokerAccounts: Array<{
        relationshipId: string;
        brokerAccountId: string | null;
        brokerAccountName: string;
        status: string;
      }>;
    }>;
    hasActiveRelationships?: boolean;
  }> => {
    const response = await apiClient.put(`/brokerage/connections/${connectionId}/primary`);
    return response.data;
  },

  updateRelationshipsForPrimary: async (connectionId: string, relationshipIds: string[]): Promise<{
    success: boolean;
    message?: string;
    updatedCount?: number;
  }> => {
    const response = await apiClient.post(`/brokerage/connections/${connectionId}/update-relationships`, {
      relationshipIds
    });
    return response.data;
  },

  syncConnection: async (connectionId: string): Promise<{
    success: boolean;
    message?: string;
    account?: { equity: number; buyingPower: number; cash: number };
    connection?: BrokerageConnection;
  }> => {
    const response = await apiClient.post(`/brokerage/connections/${connectionId}/sync`);
    return response.data;
  },

  getPositions: async (connectionId?: string): Promise<{
    success: boolean;
    positions: Array<{
      symbol: string;
      qty: number;
      avgEntryPrice: number;
      marketValue: number;
      unrealizedPl: number;
      unrealizedPlPercent: number;
      currentPrice: number;
      side: string;
    }>;
  }> => {
    const url = connectionId
      ? `/brokerage/connections/${connectionId}/positions`
      : '/brokerage/positions';
    const response = await apiClient.get(url);
    return response.data;
  },

  getOrders: async (connectionId?: string, status?: string, limit?: number, page?: number): Promise<{
    success: boolean;
    orders: Array<{
      id: string;
      symbol: string;
      side: string;
      qty: number;
      filledQty: number;
      type: string;
      status: string;
      limitPrice?: number;
      stopPrice?: number;
      filledPrice?: number;
      timeInForce: string;
      submittedAt: string;
      filledAt?: string;
    }>;
    pagination?: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> => {
    const url = connectionId
      ? `/brokerage/connections/${connectionId}/orders`
      : '/brokerage/orders';
    const response = await apiClient.get(url, { params: { status, limit, page } });
    return response.data;
  },

  disconnectConnection: async (connectionId: string): Promise<{
    success: boolean;
    message?: string;
    unfollowedCount?: number;
    unfollowedMasterIds?: string[];
  }> => {
    const response = await apiClient.delete(`/brokerage/connections/${connectionId}`);
    return response.data;
  },

  getAccount: async (connectionId?: string): Promise<{
    success: boolean;
    account: {
      id: string;
      equity: number;
      buyingPower: number;
      buying_power?: number;
      cash: number;
      portfolio_value?: number;
      currency: string;
      accountType: string;
    };
    connection: {
      _id: string;
      brokerageId: string;
      brokerageName: string;
      accountType: string;
    };
  }> => {
    const url = connectionId
      ? `/brokerage/connections/${connectionId}/account`
      : '/brokerage/account';
    const response = await apiClient.get(url);
    return response.data;
  },

  getSnapTradeBrokerages: async (): Promise<{
    success: boolean;
    count: number;
    brokerages: Array<{
      id: string;
      name: string;
      slug: string;
      description: string;
      logo: string;
      allowsTrading: boolean;
      allowsFractionalUnits: boolean;
      hasReporting: boolean;
      isRealTimeConnection: boolean;
      maintenanceMode: boolean;
      openUrl: string;
    }>;
  }> => {
    const response = await apiClient.get('/brokerage/snaptrade-brokerages');
    return response.data;
  },

  getSnaptradePortal: async (): Promise<{
    success: boolean;
    redirectUrl: string;
    redirectURI?: string;
    message?: string;
  }> => {
    const redirectBase = typeof window !== 'undefined' ? window.location.origin : '';
    const params: Record<string, string> = { reconnect: 'true' };
    if (redirectBase) params.redirectBase = redirectBase;
    const response = await apiClient.get('brokerage/snaptrade/connect-url', { params });
    return response.data;
  },

  /** Native Webull Connect API — browser OAuth → authorization code callback */
  getWebullConnectUrl: async (): Promise<{
    success: boolean;
    redirectURI?: string;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/webull/connect-url');
    return response.data;
  },

  getWebullAccessToken: async (): Promise<{
    success: boolean;
    accessToken?: string;
    accessTokenExpiresAt?: string | null;
    refreshTokenExpiresAt?: string | null;
    connectionId?: string;
    status?: string;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/webull/access-token');
    return response.data;
  },

  getWebullAccounts: async (opts?: {
    includeBalances?: boolean;
  }): Promise<{
    success: boolean;
    accounts?: Array<Record<string, unknown>>;
    count?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/webull/accounts', {
      params: {
        includeBalances: opts?.includeBalances ? 'true' : 'false',
      },
    });
    return response.data;
  },

  getWebullOrderDetail: async (payload: {
    accessToken: string;
    accountId: string;
    clientOrderId?: string;
  }): Promise<{
    success: boolean;
    order?: Record<string, unknown>;
    error?: string;
  }> => {
    const response = await apiClient.post('/brokerage/webull/order-detail', payload);
    return response.data;
  },

  getWebullOrderHistory: async (accountId: string): Promise<{
    success: boolean;
    accountId?: string;
    orders?: Array<Record<string, unknown>>;
    count?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/webull/order-history', {
      params: { accountId },
    });
    return response.data;
  },

  getWebullPositions: async (accountId: string): Promise<{
    success: boolean;
    accountId?: string;
    positions?: Array<Record<string, unknown>>;
    count?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/webull/positions', {
      params: { accountId },
    });
    return response.data;
  },

  getWebullOpenOrders: async (accountId: string): Promise<{
    success: boolean;
    accountId?: string;
    orders?: Array<Record<string, unknown>>;
    count?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/webull/open-orders', {
      params: { accountId },
    });
    return response.data;
  },

  placeWebullOrder: async (payload: {
    accountId: string;
    symbol: string;
    quantity?: string | number;
    side?: 'BUY' | 'SELL';
    optionType?: 'CALL' | 'PUT';
    expiry?: string;
  }): Promise<{
    success: boolean;
    clientOrderId?: string;
    order?: Record<string, unknown>;
    resolved?: {
      expiry?: string;
      optionType?: string;
      strike?: string;
      legSymbol?: string;
    };
    raw?: unknown;
    error?: string;
  }> => {
    const response = await apiClient.post('/brokerage/webull/place-order', payload);
    return response.data;
  },

  cancelWebullOrder: async (payload: {
    accountId: string;
    clientOrderId: string;
  }): Promise<{ success: boolean; error?: string }> => {
    const response = await apiClient.post('/brokerage/webull/cancel-order', payload);
    return response.data;
  },

  closeWebullPosition: async (payload: {
    accountId: string;
    symbol: string;
    quantity: string | number;
    instrumentType?: string;
    strike?: string | number;
    expiry?: string;
    optionType?: string;
    optionStrategy?: string;
  }): Promise<{
    success: boolean;
    clientOrderId?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/brokerage/webull/close-position', payload);
    return response.data;
  },

  connectSnaptradeBroker: async (brokerSlug: string): Promise<{
    success: boolean;
    redirectUrl: string;
    message?: string;
  }> => {
    const redirectBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const response = await apiClient.post(`/snaptrade/connect/${brokerSlug}`, { redirectBaseUrl });
    return response.data;
  },

  syncSnaptradeAccounts: async (): Promise<{
    success: boolean;
    message: string;
    connections: BrokerageConnection[];
    count: number;
    skippedDisconnectedBrokers?: string[];
  }> => {
    const response = await apiClient.post('/brokerage/connect/snaptrade/sync');
    return response.data;
  },

  /** After Webull/SnapTrade OAuth — list accounts for user to pick (Genesis-style). */
  listSnaptradeAccounts: async (): Promise<{
    success: boolean;
    message?: string;
    accounts: Array<{
      id: string;
      accountNumber: string;
      displayName?: string;
      name?: string;
      brokerageName?: string;
      currency: string;
      balance: number | null;
      accountType?: string;
      alreadyConnected?: boolean;
    }>;
    count: number;
    availableCount?: number;
    pendingAuthorizations?: number;
    error?: string;
  }> => {
    const response = await apiClient.get('/brokerage/connect/snaptrade/accounts');
    return response.data;
  },

  /** Connect one selected Webull/SnapTrade account. */
  selectSnaptradeAccount: async (
    accountId: string
  ): Promise<{
    success: boolean;
    message: string;
    data?: {
      connection: {
        id: string;
        brokerageName: string;
        connectionType: string;
        accountId?: string;
        accountNumber?: string;
        accountName?: string;
      };
    };
  }> => {
    const response = await apiClient.post('/brokerage/connect/snaptrade/select-account', {
      accountId,
    });
    return response.data;
  },

  resetSnaptrade: async (): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const response = await apiClient.post('/brokerage/reset-snaptrade');
    return response.data;
  },

  dismissAlert: async (alertId: string): Promise<{
    success: boolean;
    message?: string;
    alert?: {
      _id: string;
      dismissedBy: string[];
    };
  }> => {
    const response = await apiClient.post(`/brokerage/alerts/${alertId}/dismiss`);
    return response.data;
  },

  getActiveAlerts: async (): Promise<{
    success: boolean;
    alerts: Array<{
      _id: string;
      affectedUserId: string | { _id: string; name: string; email: string };
      brokerageConnectionId: string | { _id: string; brokerageName: string; brokerageSlug: string; status: string };
      brokerageAuthorizationId: string;
      brokerageName: string;
      dismissedBy: string[];
      createdAt: string;
    }>;
  }> => {
    const response = await apiClient.get('/brokerage/alerts/active');
    return response.data;
  },

  getAlertStatus: async (alertId: string): Promise<{
    success: boolean;
    alertId: string;
    isDismissed: boolean;
    dismissedBy: string[];
  }> => {
    const response = await apiClient.get(`/brokerage/alerts/${alertId}/status`);
    return response.data;
  }
};

